import React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery.ts';

export interface TableColumn<T> {
  label: string;
  field: keyof T | string;
  render?: (row: T) => any;
  sortable?: boolean;
  className?: string;
  width?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  page?: number;
  perPage?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
}

const ACTION_COLUMN = /^actions?$/i;

function Table<T extends { id: number | string }>({
  columns,
  data,
  sortField,
  sortDirection,
  onSort,
  page = 1,
  perPage = 10,
  total = 0,
  onPageChange,
  loading = false,
}: TableProps<T>) {
  const totalPages = Math.ceil(total / perPage);
  // Below the md breakpoint each row becomes a card; wide tables are unreadable on phones.
  const isMobile = useMediaQuery('(max-width: 767px)');

  const renderCell = (row: T, col: TableColumn<T>) =>
    col.render ? col.render(row) : (row[col.field as keyof T] as any);

  const isActionColumn = (col: TableColumn<T>) =>
    ACTION_COLUMN.test(String(col.field)) || ACTION_COLUMN.test(col.label || '');

  const dataColumns = columns.filter((col) => !isActionColumn(col));
  const actionColumns = columns.filter(isActionColumn);
  const [titleColumn, ...detailColumns] = dataColumns;
  const sortableColumns = columns.filter((col) => col.sortable);

  const pagination =
    totalPages > 1 && onPageChange ? (
      <div className="px-4 sm:px-6 py-4 border-t flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-gray-500 text-center sm:text-left">
          Showing <span className="font-medium">{Math.min((page - 1) * perPage + 1, total)}</span>
          {' '}to{' '}
          <span className="font-medium">{Math.min(page * perPage, total)}</span>
          {' '}of{' '}
          <span className="font-medium">{total}</span> records
        </div>
        <div className="flex space-x-2">
          <button
            className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </button>
          <button
            className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  if (isMobile) {
    return (
      <div className="bg-white rounded-lg shadow w-full">
        {onSort && sortableColumns.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-3 border-b">
            <label htmlFor="table-sort-field" className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sort
            </label>
            <select
              id="table-sort-field"
              className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md text-base bg-white text-gray-700"
              value={sortField || ''}
              onChange={(e) => {
                if (e.target.value && e.target.value !== sortField) onSort(e.target.value);
              }}
            >
              {!sortField && <option value="">Choose column</option>}
              {sortableColumns.map((col) => (
                <option key={col.field as string} value={col.field as string}>
                  {col.label}
                </option>
              ))}
            </select>
            {sortField && (
              <button
                type="button"
                className="px-3 py-2 border border-gray-300 rounded-md text-base text-gray-700 bg-white hover:bg-gray-50"
                aria-label={sortDirection === 'asc' ? 'Sorted ascending, switch to descending' : 'Sorted descending, switch to ascending'}
                onClick={() => onSort(sortField)}
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 text-gray-400">No data found.</div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((row) => (
              <div key={row.id} className="p-4 space-y-2">
                {titleColumn && <div className="font-medium break-words">{renderCell(row, titleColumn)}</div>}
                {detailColumns.length > 0 && (
                  <dl className="space-y-1.5">
                    {detailColumns.map((col) => {
                      const value = renderCell(row, col);
                      return (
                        <div key={col.field as string} className="flex items-start justify-between gap-4 text-sm">
                          <dt className="flex-shrink-0 text-gray-500">{col.label}</dt>
                          <dd className="min-w-0 text-right break-words">
                            {value === null || value === undefined || value === '' ? '—' : value}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                )}
                {actionColumns.length > 0 && (
                  <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-gray-100">
                    {actionColumns.map((col) => (
                      <React.Fragment key={col.field as string}>{renderCell(row, col)}</React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {pagination}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow w-full overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.field as string}
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.className || ''} ${col.sortable ? 'cursor-pointer' : ''}`}
                style={col.width ? { width: col.width } : {}}
                onClick={col.sortable && onSort ? () => onSort(col.field as string) : undefined}
              >
                {col.label}
                {col.sortable && sortField === col.field && (
                  <span>{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-gray-400">Loading...</td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-gray-400">No data found.</td>
            </tr>
          ) : (
            data.map((row) => (
              <tr key={row.id}>
                {columns.map((col) => (
                  <td key={col.field as string} className={`px-6 py-4 whitespace-nowrap ${col.className || ''}`} style={col.width ? { width: col.width } : {}}>
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {pagination}
    </div>
  );
}

export default Table;
